import { useEffect, useState } from "react";
import { useAutoOpen } from "@/hooks/useAutoOpen";
import { api, formatApiError } from "@/lib/api";
import { rupiah, tanggalID } from "@/lib/format";
import { PageHeader } from "@/components/app/Layout";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Flame, Pencil, Trash2, PackageCheck } from "lucide-react";
import Pagination, { usePagination } from "@/components/app/Pagination";
import { StokKayuBar, rit } from "@/components/app/bakar/StokKayuBar";
import { MulaiBakarDialog } from "@/components/app/bakar/MulaiBakarDialog";
import { BongkarDialog } from "@/components/app/bakar/BongkarDialog";
import { BatchAktifCard } from "@/components/app/bakar/BatchAktifCard";

export default function BatchBakar() {
  const [items, setItems] = useState([]);
  const [stok, setStok] = useState(null);
  const [produkList, setProdukList] = useState([]);
  const [bakarOpen, setBakarOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [bongkarItem, setBongkarItem] = useState(null);

  const load = async () => {
    try {
      const [a, b, k] = await Promise.all([api.get("/batch-bakar"), api.get("/stok-kayu"), api.get("/kategori", { params: { tipe: "produk" } })]);
      setItems(a.data); setStok(b.data); setProdukList(k.data.filter((p) => p.sumber === "tungku"));
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditItem(null); setBakarOpen(true); };
  useAutoOpen(openNew);
  const openEdit = (b) => { setEditItem(b); setBakarOpen(true); };
  const del = async (b) => {
    const msg = b.status === "selesai"
      ? "Hapus batch ini? Data produksi & pengeluaran operasional terkait ikut dihapus, kayu kembali ke stok."
      : "Batalkan batch bakar ini? Kayu kembali ke stok.";
    if (!window.confirm(msg)) return;
    try { await api.delete(`/batch-bakar/${b.id}`); toast.success("Terhapus"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const aktif = items.filter((b) => b.status === "dibakar");
  const selesai = items.filter((b) => b.status === "selesai");
  const { page, setPage, totalPages, paged, total, pageSize } = usePagination(selesai, 10);

  return (<>
    <PageHeader title="Batch Bakar" subtitle="Tahap 2 & 3 — isi tungku dari stok kayu, lalu bongkar & catat hasil arang + kulitan setelah matang."
      actions={<Button onClick={openNew} className="brand-btn rounded-sm" data-testid="mulai-bakar-btn"><Flame size={14} className="mr-1"/>Mulai Bakar</Button>}/>
    <div className="p-4 md:p-8 space-y-6">
      <StokKayuBar stok={stok}/>

      <section className="space-y-3" data-testid="batch-aktif-section">
        <h3 className="text-sm md:text-base font-semibold text-slate-900">Sedang Dibakar ({aktif.length})</h3>
        {aktif.length === 0
          ? <div className="card-flat p-6 text-center text-sm text-slate-400" data-testid="batch-aktif-empty">Tidak ada tungku yang sedang dibakar. Klik "Mulai Bakar" saat kayu masuk tungku.</div>
          : <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-5">
              {aktif.map((b) => <BatchAktifCard key={b.id} b={b} onBongkar={setBongkarItem} onEdit={openEdit} onDelete={del}/>)}
            </div>}
      </section>

      <section className="space-y-3">
        <h3 className="text-sm md:text-base font-semibold text-slate-900">Riwayat Bongkar</h3>
        <div className="card-flat overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-xs md:text-sm" data-testid="batch-selesai-table">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200 text-left text-[11px] uppercase tracking-wide text-slate-500">
                  <th className="p-3">Mulai</th><th className="p-3">Bongkar</th><th className="p-3">Produk</th>
                  <th className="p-3 text-right">Kayu</th><th className="p-3 text-right">Biaya Kayu</th><th className="p-3 text-right">Operasional</th>
                  <th className="p-3 text-right">Hasil</th><th className="p-3 text-right">Kulitan</th><th className="p-3 text-right">Rendemen</th><th className="p-3 text-right">HPP/Kg</th>
                  <th className="p-3 text-right">Aksi</th>
                </tr>
              </thead>
              <tbody>
                {selesai.length === 0 && <tr><td colSpan="11" className="text-center py-10 text-slate-400">Belum ada batch yang dibongkar.</td></tr>}
                {paged.map((b) => (
                  <tr key={b.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`batch-row-${b.id}`}>
                    <td className="p-3 whitespace-nowrap">{tanggalID(b.tanggal_mulai)}</td>
                    <td className="p-3 whitespace-nowrap">{tanggalID(b.tanggal_bongkar)}</td>
                    <td className="p-3"><span className="text-[11px] px-2 py-0.5 rounded-sm bg-slate-100 text-slate-700 whitespace-nowrap">{b.produk_nama}</span></td>
                    <td className="p-3 text-right font-mono">{rit(b.jumlah_rit)}</td>
                    <td className="p-3 text-right font-mono">{rupiah(b.biaya_kayu)}</td>
                    <td className="p-3 text-right font-mono" data-testid={`batch-op-${b.id}`}>{rupiah(b.total_operasional ?? b.biaya_operasional)}</td>
                    <td className="p-3 text-right font-mono font-semibold">{Number(b.output_kg || 0).toLocaleString("id-ID")} Kg</td>
                    <td className="p-3 text-right font-mono" data-testid={`batch-kulitan-${b.id}`}>{Number(b.kulitan_kg || 0).toLocaleString("id-ID")} Kg</td>
                    <td className="p-3 text-right font-mono">{Number(b.rendemen_kg_per_rit || 0).toLocaleString("id-ID")} Kg/rit</td>
                    <td className="p-3 text-right font-mono brand-text font-semibold" data-testid={`batch-hpp-${b.id}`}>{rupiah(b.hpp_per_kg)}</td>
                    <td className="p-3">
                      <div className="flex items-center gap-1 justify-end">
                        <button onClick={() => setBongkarItem(b)} title="Ubah hasil bongkar" className="p-1.5 rounded-sm hover:bg-emerald-50" data-testid={`edit-bongkar-${b.id}`}><PackageCheck size={14} className="text-emerald-600"/></button>
                        <button onClick={() => openEdit(b)} title="Ubah data bakar" className="p-1.5 rounded-sm hover:bg-slate-100" data-testid={`edit-batch-${b.id}`}><Pencil size={14}/></button>
                        <button onClick={() => del(b)} className="p-1.5 rounded-sm hover:bg-rose-50" data-testid={`del-batch-${b.id}`}><Trash2 size={14} className="text-rose-600"/></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination page={page} setPage={setPage} totalPages={totalPages} total={total} pageSize={pageSize} testid="batch-pagination"/>
        </div>
      </section>
    </div>

    <MulaiBakarDialog open={bakarOpen} setOpen={setBakarOpen} item={editItem} stok={stok} produkList={produkList} onSaved={load}/>
    <BongkarDialog open={!!bongkarItem} setOpen={(v) => !v && setBongkarItem(null)} item={bongkarItem} onSaved={load}/>
  </>);
}
